/**
 * Item effect VFX — client-side visual feedback for on-hit weapon procs (Tasks 2–3).
 *
 * This module actually RENDERS (it is not just data): the floater manager calls
 * `tickWeaponDotVfx` once per cube per frame. The module:
 *
 *   1. Detects HP drops while a cube carries a weapon-DoT buff
 *      (`cube.spellbook.buffs`, synced via Colyseus delta) and spawns a floating
 *      damage number at the unit's screen position, tinted per the DoT kind
 *      (poison=green, fire=orange, bash=gold).
 *   2. Returns the dominant active DoT kind so the manager can set a debuff tint
 *      (`data-dot=` on the rich-overlay root → CSS glow on the name tag).
 *
 * Task 3 additions:
 *   3. `tickLifestealVfx(cube, sessionId, layer, screenX, screenY)` — call on the
 *      ATTACKER after a hit. Detects HP gains (lifesteal heal) and spawns a
 *      green "+N" floater. Tracks last-seen HP in `lastHpByCubeHeal`.
 *   4. `spawnAuraHealVfx(layer, screenX, screenY, amount)` — call when a Celestial
 *      aura-heal fires. Spawns a green "+N" floater on the ALLY + a short heal-beam
 *      DOM element that fades out. The caller (the Colyseus state listener for hp
 *      changes + CelestialOrbCooldown buff appearance) drives this.
 *
 * The buff-icon strip is handled by the rich-overlay's existing behavior strip:
 * the manager merges the weapon-DoT buff ids into the `behaviors` Map it hands
 * the overlay, and `floaters/registry.ts` carries their strip-icon presentation.
 * So a poisoned unit shows: a pulsing green poison-drop icon in its strip, a green
 * glow on its name, and green rising damage numbers each tick.
 *
 * Server-side counterpart: `game/server/src/items/combat-procs.ts`.
 */

import * as THREE from "three";
import type { Cube } from "@tribez/shared";
import { createPointBurst, type VfxInstance } from "../abilities/cast-vfx-shapes";

export type DotVfxKind = "poison" | "fire" | "bash";

export interface DotVfxSpec {
  kind: DotVfxKind;
  /** CSS color for the floating damage number text. */
  color: string;
  /** data-dot value for the overlay tint (matches CSS `.fl-root[data-dot=...]`). */
  tint: "poison" | "fire" | "bash";
}

/**
 * Buff id → VFX spec. Keys match the server BUFF_DEFS ids applied by the proc bus.
 * Adding a new weapon DoT buff: add a row here + a registry.ts strip-icon entry.
 */
export const WEAPON_DOT_VFX: Readonly<Record<string, DotVfxSpec>> = {
  NoxiousVenom:           { kind: "poison", color: "#7cff6a", tint: "poison" },
  VenomousArbalestPoison: { kind: "poison", color: "#a6ff5c", tint: "poison" },
  UndyingFlames:          { kind: "fire",   color: "#ff8a1e", tint: "fire" },
  FelFlames:              { kind: "fire",   color: "#ff5a2e", tint: "fire" },
  RollingPinLifePact:     { kind: "bash",   color: "#ffce4d", tint: "bash" },
};

export function isWeaponDotBuff(buffId: string): boolean {
  return buffId in WEAPON_DOT_VFX;
}

export function getWeaponDotVfx(buffId: string): DotVfxSpec | null {
  return WEAPON_DOT_VFX[buffId] ?? null;
}

// ---------------------------------------------------------------------------
// Floater rendering
// ---------------------------------------------------------------------------

/** Per-cube last-seen HP, so we only float on a DROP (not regen / refill). */
const lastHpByCube = new Map<string, number>();

/**
 * Spawn one rising, fading floating damage number at (screenX, screenY).
 * Self-removes after the CSS animation (0.9s).
 */
function spawnDotFloater(
  layer: HTMLElement,
  screenX: number,
  screenY: number,
  text: string,
  color: string,
): void {
  const el = document.createElement("div");
  el.className = "fl-dot-floater";
  el.style.left = `${Math.round(screenX)}px`;
  el.style.top = `${Math.round(screenY)}px`;
  el.style.color = color;
  el.textContent = text;
  layer.appendChild(el);
  // Remove after the animation completes (matches fl-dot-rise 0.9s).
  window.setTimeout(() => el.remove(), 950);
}

/**
 * Per-cube per-frame VFX step. Called by the floater manager inside its cube
 * loop. Detects HP drops while a weapon-DoT buff is active and floats a colored
 * damage number; returns the dominant DoT tint for the overlay (or null).
 *
 * @param cube        The cube schema (reads `cube.hp`, `cube.spellbook.buffs`).
 * @param sessionId   Cube key (for the per-cube HP cache).
 * @param layer       The `#floaters-layer` DOM element.
 * @param screenX     Projected screen X of the unit's head.
 * @param screenY     Projected screen Y of the unit's head.
 * @returns           The dominant DoT tint ("poison"|"fire"|"bash") or null.
 */
export function tickWeaponDotVfx(
  cube: Cube,
  sessionId: string,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): "poison" | "fire" | "bash" | null {
  // Find the active weapon-DoT buffs on this cube.
  let dominant: DotVfxSpec | null = null;
  cube.spellbook?.buffs?.forEach((_inst, buffId) => {
    const spec = WEAPON_DOT_VFX[buffId];
    if (spec && !dominant) dominant = spec;
  });

  const prevHp = lastHpByCube.get(sessionId);
  const curHp = cube.hp;
  lastHpByCube.set(sessionId, curHp);

  // Float a colored number on an HP drop while a weapon DoT is active.
  // (We can't isolate the DoT tick from other simultaneous damage on the client;
  //  attributing the drop to the active DoT is the lean, KTS-visible choice —
  //  a burning unit shows orange numbers as it loses HP.)
  if (dominant && layer && prevHp !== undefined && curHp < prevHp) {
    const drop = prevHp - curHp;
    const spec = dominant as DotVfxSpec;
    spawnDotFloater(layer, screenX, screenY, `-${drop}`, spec.color);
  }

  return dominant ? (dominant as DotVfxSpec).tint : null;
}

/** Drop a cube's HP cache when it dies / leaves (manager calls on cull). */
export function clearWeaponDotVfx(sessionId: string): void {
  lastHpByCube.delete(sessionId);
}

// ---------------------------------------------------------------------------
// Task 3: Lifesteal + Celestial aura-heal VFX
// ---------------------------------------------------------------------------

/** Green heal color for lifesteal / aura-heal floaters. */
const HEAL_COLOR = "#4dff88";
/** Lighter green for aura-heal beam accent. */
const AURA_HEAL_BEAM_COLOR = "#80ffb3";

/** Per-cube last-seen HP for HEAL detection (separate from damage tracking). */
const lastHpByCubeHeal = new Map<string, number>();

/**
 * Lifesteal VFX step. Call once per frame on the ATTACKER cube.
 * Detects HP gains (lifesteal heal) and spawns a green "+N" floater.
 *
 * Note: we detect any HP increase — this could theoretically double-float
 * if the attacker simultaneously receives a heal from another source on the
 * same frame. In practice the lifesteal path is rare enough that false
 * positives are acceptable in v1. A future pass can gate on active lifesteal
 * item buff. We DO skip the frame an aura-heal landed (CelestialOrbCooldown
 * present) so the Celestial heal doesn't double-float (tickAuraHealVfx owns it).
 */
export function tickLifestealVfx(
  cube: Cube,
  sessionId: string,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): void {
  const prevHp = lastHpByCubeHeal.get(sessionId);
  const curHp = cube.hp;
  lastHpByCubeHeal.set(sessionId, curHp);

  if (layer && prevHp !== undefined && curHp > prevHp) {
    // Defer to the aura-heal VFX on the frame a Celestial heal lands.
    if (cube.spellbook?.buffs?.has(CELESTIAL_ICD_BUFF)) return;
    const gained = curHp - prevHp;
    spawnHealFloater(layer, screenX, screenY, `+${gained}`, HEAL_COLOR);
  }
}

/** Drop a cube's heal-HP cache when it dies / leaves (manager calls on cull). */
export function clearLifestealVfx(sessionId: string): void {
  lastHpByCubeHeal.delete(sessionId);
}

/**
 * Spawn a green heal floater. Used for both lifesteal (on attacker) and
 * aura-heal (on ally). Caller provides screen position.
 *
 * Element: `div.fl-heal-floater` — CSS handles the animation.
 * Self-removes after 0.9s (matches fl-dot-rise animation duration).
 */
function spawnHealFloater(
  layer: HTMLElement,
  screenX: number,
  screenY: number,
  text: string,
  color: string,
): void {
  const el = document.createElement("div");
  el.className = "fl-heal-floater";
  el.style.left = `${Math.round(screenX)}px`;
  el.style.top = `${Math.round(screenY)}px`;
  el.style.color = color;
  el.textContent = text;
  layer.appendChild(el);
  window.setTimeout(() => el.remove(), 950);
}

/** The Celestial aura-heal ICD buff id (server BUFF_DEFS key). Its presence on an
 *  ally signals the aura-heal just fired (the heal + ICD stamp happen together). */
const CELESTIAL_ICD_BUFF = "CelestialOrbCooldown";

/** Per-cube last-seen HP for AURA-HEAL detection (ally-heal; separate cache). */
const lastHpByCubeAura = new Map<string, number>();

/**
 * Celestial aura-heal VFX step. Call once per frame on EVERY ally cube (mirrors
 * the lifesteal detection pattern — NOT a Colyseus listener). Detects an HP gain
 * while the cube carries the `CelestialOrbCooldown` ICD buff (the buff's presence
 * marks the frame the heal landed) and spawns the aura-heal VFX on that ally.
 *
 * @param cube      The ally cube schema (reads `cube.hp`, `cube.spellbook.buffs`).
 * @param sessionId Cube key (for the per-cube HP cache).
 * @param layer     The `#floaters-layer` DOM element.
 * @param screenX   Screen X of the ally's head.
 * @param screenY   Screen Y of the ally's head.
 */
export function tickAuraHealVfx(
  cube: Cube,
  sessionId: string,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): void {
  const prevHp = lastHpByCubeAura.get(sessionId);
  const curHp = cube.hp;
  lastHpByCubeAura.set(sessionId, curHp);

  if (!layer || prevHp === undefined || curHp <= prevHp) return;
  // Only attribute the gain to a Celestial heal when the ICD buff is present —
  // it's stamped on the ally in the same server tick the heal lands.
  if (!cube.spellbook?.buffs?.has(CELESTIAL_ICD_BUFF)) return;

  const gained = curHp - prevHp;
  spawnAuraHealVfx(layer, screenX, screenY, gained);
}

/** Drop a cube's aura-heal HP cache when it dies / leaves (manager calls on cull). */
export function clearAuraHealVfx(sessionId: string): void {
  lastHpByCubeAura.delete(sessionId);
}

// ---------------------------------------------------------------------------
// Task 4: Warmth-restore floater
// ---------------------------------------------------------------------------

/** Warm orange color for warmth-restore floaters — distinct from green heal. */
const WARMTH_FLOATER_COLOR = "#ff9b3a";

/** Per-cube last-seen warmth, for detecting gains (food restores). */
const lastWarmthByCube = new Map<string, number>();

/**
 * Warmth-restore VFX step. Call once per frame on the local player's cube (own
 * cube only — we only care about warmth changes on the player themselves).
 *
 * Detects warmth GAINS (food restores, debug setMeter) and spawns an orange
 * "+N Warmth" floater. The warmth HUD meter already reflects any drain-reducer
 * effect (drains more slowly), so no additional VFX is needed for that path.
 *
 * @param cube      The cube schema (reads `cube.survival.warmth`).
 * @param sessionId Cube key (for the per-cube warmth cache).
 * @param layer     The `#floaters-layer` DOM element.
 * @param screenX   Projected screen X of the unit's head.
 * @param screenY   Projected screen Y of the unit's head.
 */
export function tickWarmthVfx(
  cube: Cube,
  sessionId: string,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): void {
  const curWarmth = cube.survival?.warmth ?? 100;
  const prevWarmth = lastWarmthByCube.get(sessionId);
  lastWarmthByCube.set(sessionId, curWarmth);

  if (layer && prevWarmth !== undefined && curWarmth > prevWarmth) {
    const gained = Math.round(curWarmth - prevWarmth);
    if (gained > 0) {
      spawnWarmthFloater(layer, screenX, screenY, `+${gained}`);
    }
  }
}

/** Drop a cube's warmth cache when it dies / leaves (manager calls on cull). */
export function clearWarmthVfx(sessionId: string): void {
  lastWarmthByCube.delete(sessionId);
}

/**
 * Spawn a warmth-restore floater (orange "+N") at the given screen position.
 * Exported for direct use in testing.
 */
export function spawnWarmthFloater(
  layer: HTMLElement,
  screenX: number,
  screenY: number,
  text: string,
): void {
  const el = document.createElement("div");
  el.className = "fl-warmth-floater";
  el.style.left = `${Math.round(screenX)}px`;
  el.style.top = `${Math.round(screenY)}px`;
  el.style.color = WARMTH_FLOATER_COLOR;
  el.textContent = text;
  layer.appendChild(el);
  window.setTimeout(() => el.remove(), 950);
}

// ---------------------------------------------------------------------------
// Task 6: CC state overlay icons — stun / daze / slow / silence / fear
// ---------------------------------------------------------------------------

/**
 * CC kind shown over a cube's head when incapacitated.
 * Null = no CC active.
 */
export type CcOverlayKind = "stun" | "slow" | "daze" | "silence" | "fear" | null;

/**
 * Buff id → CC overlay kind. Keys match server BUFF_DEFS ids applied by CC items.
 * Adding a new CC buff: add a row here.
 */
export const CC_OVERLAY_BUFFS: Readonly<Record<string, CcOverlayKind>> = {
  // Task 5 generics
  GenericStun:          "stun",
  Feared:               "fear",
  Silenced:             "silence",
  // Task 6 item CC buffs
  MurlocStun:           "stun",
  HeartpiercerStun:     "stun",
  BurningSensationStun: "stun",
  DrunkenBoozeDebuff:   "daze",
  IndigestionSlow:      "slow",
  ReapersScytheSilence: "silence",
  DreadplateFear:       "fear",
};

/**
 * Per-frame CC overlay step. Called by the floater manager inside its cube loop.
 * Checks the cube's active buffs for any CC buff, creates/updates/removes a
 * `.fl-cc-overlay` DOM element above the unit's head.
 *
 * @param cube      The cube schema (reads `cube.spellbook.buffs`).
 * @param sessionId Cube key (used as the overlay element's id suffix).
 * @param layer     The `#floaters-layer` DOM element.
 * @param screenX   Projected screen X of the unit's head.
 * @param screenY   Projected screen Y of the unit's head.
 * @returns         The active CC kind, or null if none.
 */
export function tickCcOverlayVfx(
  cube: Cube,
  sessionId: string,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): CcOverlayKind {
  // Scan buffs for the highest-priority CC (stun > fear > silence > daze > slow).
  let kind: CcOverlayKind = null;
  cube.spellbook?.buffs?.forEach((_inst, buffId) => {
    if (kind === "stun") return; // highest — stop scanning
    const k = CC_OVERLAY_BUFFS[buffId];
    if (!k) return;
    // Priority: stun > fear > silence > daze > slow
    const priorities: Record<string, number> = { stun: 5, fear: 4, silence: 3, daze: 2, slow: 1 };
    if ((priorities[k] ?? 0) > (priorities[kind ?? ""] ?? 0)) kind = k;
  });

  const overlayId = `cc-overlay-${sessionId}`;

  if (kind && layer) {
    let el = document.getElementById(overlayId) as HTMLElement | null;
    if (!el) {
      el = document.createElement("div");
      el.id = overlayId;
      el.className = "fl-cc-overlay";
      layer.appendChild(el);
    }
    el.dataset.cc = kind;
    el.style.left = `${Math.round(screenX) - 12}px`;
    el.style.top = `${Math.round(screenY) - 36}px`;
  } else {
    document.getElementById(overlayId)?.remove();
  }

  return kind;
}

/** Remove the CC overlay DOM element when a cube dies or leaves state. */
export function clearCcOverlayVfx(sessionId: string): void {
  document.getElementById(`cc-overlay-${sessionId}`)?.remove();
}

/**
 * Per-frame CC overlay step for CREATURES (item-effects creature-CC). Creatures
 * have no buff store, so the CC kind isn't readable per-buff — instead the
 * server syncs `creature.incapUntil` (epoch-ms until any stun/disarm/slow ends).
 * While that's in the future we show a generic "stun" overlay above the
 * creature's head (the stun-stars marker — slow/disarm share the same icon
 * since the creature can't surface a per-kind buff strip). Reuses the same
 * `.fl-cc-overlay` element + cull path as the cube overlay, keyed by creatureId.
 *
 * @param incapUntil Server epoch-ms from `creature.incapUntil` (0 = no CC).
 * @param now        Client Date.now().
 */
export function tickCreatureCcOverlayVfx(
  creatureId: string,
  incapUntil: number,
  now: number,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): void {
  const overlayId = `cc-overlay-creature-${creatureId}`;
  const active = incapUntil > now;
  if (active && layer) {
    let el = document.getElementById(overlayId) as HTMLElement | null;
    if (!el) {
      el = document.createElement("div");
      el.id = overlayId;
      el.className = "fl-cc-overlay";
      layer.appendChild(el);
    }
    el.dataset.cc = "stun";
    el.style.left = `${Math.round(screenX) - 12}px`;
    el.style.top = `${Math.round(screenY) - 36}px`;
  } else {
    document.getElementById(overlayId)?.remove();
  }
}

/** Remove the creature CC overlay element when a creature dies or leaves state. */
export function clearCreatureCcOverlayVfx(creatureId: string): void {
  document.getElementById(`cc-overlay-creature-${creatureId}`)?.remove();
}

/**
 * Per-frame DoT overlay step for CREATURES (item-effects creature-DoT). Creatures
 * have no buff store / buff strip, so a poisoned/burning bear can't surface a
 * debuff icon. Instead the server syncs `creature.dotUntil` (epoch-ms until the
 * DoT ends) + `creature.dotKind` ("poison"|"fire"|"bash"). While that's in the
 * future we render a `.fl-creature-dot` marker above the creature's head — a
 * pulsing colored drop/flame keyed by kind (poison=green, fire=orange). Reuses
 * the same create/update/cull DOM pattern as the creature CC overlay, keyed by
 * creatureId.
 *
 * @param dotUntil Server epoch-ms from `creature.dotUntil` (0 = no DoT).
 * @param dotKind  `creature.dotKind` ("poison"|"fire"|"bash" or "").
 * @param now      Client Date.now().
 */
export function tickCreatureDotOverlayVfx(
  creatureId: string,
  dotUntil: number,
  dotKind: string,
  now: number,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): void {
  const overlayId = `dot-overlay-creature-${creatureId}`;
  const active = dotUntil > now && dotKind !== "";
  if (active && layer) {
    let el = document.getElementById(overlayId) as HTMLElement | null;
    if (!el) {
      el = document.createElement("div");
      el.id = overlayId;
      el.className = "fl-creature-dot";
      layer.appendChild(el);
    }
    el.dataset.dot = dotKind;
    // Offset to the right of the CC overlay so a stunned + poisoned creature
    // shows both markers side by side.
    el.style.left = `${Math.round(screenX) + 12}px`;
    el.style.top = `${Math.round(screenY) - 36}px`;
  } else {
    document.getElementById(overlayId)?.remove();
  }
}

/** Remove the creature DoT overlay element when a creature dies or leaves state. */
export function clearCreatureDotOverlayVfx(creatureId: string): void {
  document.getElementById(`dot-overlay-creature-${creatureId}`)?.remove();
}

// ---------------------------------------------------------------------------
// Creature DoT damage-number floaters
// ---------------------------------------------------------------------------

/** Per-creature last-seen HP for DoT number floater detection. */
const lastHpByCreature = new Map<string, number>();

/** Color per dotKind for the creature number floater. Matches WEAPON_DOT_VFX colors. */
const CREATURE_DOT_COLORS: Record<string, string> = {
  poison: "#7cff6a",
  fire:   "#ff8a1e",
  bash:   "#ffce4d",
};

/**
 * Per-frame creature DoT number floater. Call once per creature per frame.
 * Mirrors `tickWeaponDotVfx` for creatures: detects HP drops while the
 * creature's `dotUntil` is in the future and spawns a rising colored `-N`
 * number tinted by `dotKind` (poison=green, fire=orange, bash=gold).
 *
 * @param creatureId  Creature key (for per-creature HP cache).
 * @param hp          Current creature HP.
 * @param dotUntil    Server epoch-ms until DoT ends (0 = none).
 * @param dotKind     "poison" | "fire" | "bash" | "".
 * @param layer       The `#floaters-layer` DOM element.
 * @param screenX     Projected screen X of the creature head.
 * @param screenY     Projected screen Y of the creature head.
 */
export function tickCreatureDotVfx(
  creatureId: string,
  hp: number,
  dotUntil: number,
  dotKind: string,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): void {
  const prevHp = lastHpByCreature.get(creatureId);
  lastHpByCreature.set(creatureId, hp);

  const dotActive = dotUntil > Date.now() && dotKind !== "";
  if (dotActive && layer && prevHp !== undefined && hp < prevHp) {
    const drop = prevHp - hp;
    const color = CREATURE_DOT_COLORS[dotKind] ?? "#ffffff";
    spawnDotFloater(layer, screenX, screenY, `-${drop}`, color);
  }
}

/** Drop a creature's HP cache when it dies or leaves state. */
export function clearCreatureDotVfx(creatureId: string): void {
  lastHpByCreature.delete(creatureId);
}

/**
 * Spawn Celestial aura-heal VFX on an ALLY:
 *   1. A green "+N" heal floater.
 *   2. A short heal-beam / sparkle element that fades out (DOM div with a
 *      radial-gradient glow, CSS-animated).
 *
 * Normally driven by `tickAuraHealVfx`; exported for direct use / testing.
 *
 * @param layer    The `#floaters-layer` DOM element.
 * @param screenX  Screen X of the healed ally.
 * @param screenY  Screen Y of the healed ally.
 * @param amount   HP gained (for the floater text).
 */
export function spawnAuraHealVfx(
  layer: HTMLElement,
  screenX: number,
  screenY: number,
  amount: number,
): void {
  // Heal floater (green "+N").
  spawnHealFloater(layer, screenX, screenY, `+${amount}`, HEAL_COLOR);

  // Heal-beam / sparkle: a short glowing element above the unit, CSS-animated to
  // fade out in 0.65s. Purely DOM/CSS for this short-lived element.
  const beam = document.createElement("div");
  beam.className = "fl-aura-heal-beam";
  beam.style.left = `${Math.round(screenX)}px`;
  beam.style.top = `${Math.round(screenY - 20)}px`;
  beam.style.background = `radial-gradient(ellipse at center, ${AURA_HEAL_BEAM_COLOR} 0%, transparent 70%)`;
  layer.appendChild(beam);
  window.setTimeout(() => beam.remove(), 700);
}

// ---------------------------------------------------------------------------
// Task 8: Item summon spawn-puff VFX
// ---------------------------------------------------------------------------

/**
 * Spawn a "puff" burst VFX at the given screen position when an item summon appears.
 * Used by the creature-render or state-listener to mark the instant a summoned
 * creature enters state.creatures owned by the local player.
 *
 * Visual: a dark-purple expanding ring with a short fade, distinct from the
 * heal-beam (green) and the aura-heal (teal). Color chosen to match "dark necromancy"
 * theme of Demonward/ReapersHand/AbysmalClaw.
 *
 * @param layer     The `#floaters-layer` DOM element.
 * @param screenX   Screen X of the spawn point (creature's projected head).
 * @param screenY   Screen Y of the spawn point.
 */
export function spawnSummonPuffVfx(
  layer: HTMLElement,
  screenX: number,
  screenY: number,
): void {
  const el = document.createElement("div");
  el.className = "fl-summon-puff";
  el.style.left = `${Math.round(screenX) - 16}px`;
  el.style.top = `${Math.round(screenY) - 16}px`;
  layer.appendChild(el);
  // Self-remove after the CSS animation (0.55s).
  window.setTimeout(() => el.remove(), 600);
}

/**
 * Per-creature summon-puff detector. Call once per frame for creatures owned
 * by the local player. Detects NEW creatures (first time seen) and spawns a
 * puff at their position. Uses a Set<string> of known creatureIds so it only
 * fires once per creature, on its first appearance.
 *
 * Caller (floater manager or creature renderer) maintains the `knownIds` Set
 * and clears entries when a creature is removed from state.
 *
 * @param creatureId   The creature schema id.
 * @param knownIds     Caller-owned Set tracking already-puffed creatures.
 * @param isOwn        True when creature.ownerId === mySessionId.
 * @param layer        The `#floaters-layer` DOM element.
 * @param screenX      Projected screen X.
 * @param screenY      Projected screen Y.
 */
export function tickSummonPuffVfx(
  creatureId: string,
  knownIds: Set<string>,
  isOwn: boolean,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): void {
  if (!isOwn) return;
  if (knownIds.has(creatureId)) return;
  knownIds.add(creatureId);
  if (layer) spawnSummonPuffVfx(layer, screenX, screenY);
}

/** Remove a creature from the puff-tracker when it leaves state. */
export function clearSummonPuffVfx(creatureId: string, knownIds: Set<string>): void {
  knownIds.delete(creatureId);
}

// ---------------------------------------------------------------------------
// Task 9: Item dash VFX (StagHuntersStiletto charge / ForestersMace leap)
//
// Scene-based (3D), not DOM floaters: a dash is a fast world-space motion, so
// the trail + arrival impact render in the world. The render loop detects a
// dash by per-frame world-space position delta (a dashing cube moves ~12 u/s,
// far above the ~2.5 u/s walk speed → the per-frame delta clears DASH_SPEED_EPS
// for several frames). `tickDashTrailVfx` spawns a fading trail puff behind the
// cube each frame while dashing, and an arrival burst the frame the cube stops.
// Both push VfxInstances onto the caller-owned `out` array; the render loop's
// existing one-shot-VFX sweep ticks + culls them (same as projectilePoofs).
// ---------------------------------------------------------------------------

/** Per-second speed above which a cube is considered "dashing" (walk ≈ 2.5 u/s,
 *  dash ≈ 12 u/s; 6 u/s cleanly separates them). */
const DASH_SPEED_EPS = 6;
/** Trail-puff color (cyan-white motion streak). */
const DASH_TRAIL_COLOR = 0x9fe8ff;
/** Arrival-impact color (bright flash ring). */
const DASH_ARRIVAL_COLOR = 0xffffff;

/** Per-cube last world position + a frame counter to throttle trail spawns. */
interface DashTrack { x: number; z: number; dashing: boolean; frame: number; }
const dashTrackByCube = new Map<string, DashTrack>();

/**
 * Spawn a single fading trail puff at (x, y, z) and push it onto `out`.
 * A small flat ring decal that fades quickly — reads as a motion afterimage.
 * Exported for direct use / testing.
 */
export function startDashTrail(out: VfxInstance[], scene: THREE.Scene, x: number, y: number, z: number): void {
  const puff = createPointBurst({ color: DASH_TRAIL_COLOR, radius: 0.55, durationMs: 320 });
  puff.object.position.set(x, y + 0.05, z);
  scene.add(puff.object);
  out.push(puff);
}

/**
 * Spawn the arrival-impact burst at (x, y, z) — a bright expanding ring.
 * Exported for direct use / testing.
 */
export function triggerDashArrival(out: VfxInstance[], scene: THREE.Scene, x: number, y: number, z: number): void {
  const burst = createPointBurst({ color: DASH_ARRIVAL_COLOR, radius: 1.4, durationMs: 450 });
  burst.object.position.set(x, y + 0.05, z);
  scene.add(burst.object);
  out.push(burst);
}

/**
 * Per-frame dash detector. Call once per cube in the render loop with the cube's
 * CURRENT rendered world position. Detects dash motion by position delta:
 *   - while the per-frame speed is above DASH_SPEED_EPS → spawn a trail puff
 *     (throttled to every other frame to keep the count sane);
 *   - on the frame the speed drops back below the threshold AFTER dashing →
 *     spawn the arrival burst.
 *
 * @param sessionId Cube key (per-cube position cache).
 * @param x,y,z     Current rendered world position of the cube.
 * @param dtMs      Frame delta in ms (to convert delta → speed).
 * @param scene     The three.js scene.
 * @param out       Caller-owned VfxInstance array (render loop ticks + culls).
 */
export function tickDashTrailVfx(
  sessionId: string,
  x: number, y: number, z: number,
  dtMs: number,
  scene: THREE.Scene,
  out: VfxInstance[],
): void {
  const prev = dashTrackByCube.get(sessionId);
  if (!prev) {
    dashTrackByCube.set(sessionId, { x, z, dashing: false, frame: 0 });
    return;
  }
  const dx = x - prev.x;
  const dz = z - prev.z;
  const dist = Math.hypot(dx, dz);
  const speed = dtMs > 0 ? dist / (dtMs / 1000) : 0;
  const nowDashing = speed > DASH_SPEED_EPS;

  if (nowDashing) {
    prev.frame = (prev.frame + 1) % 2;
    if (prev.frame === 0) startDashTrail(out, scene, prev.x, y, prev.z);
  } else if (prev.dashing) {
    // Just stopped dashing → arrival burst at the landing point.
    triggerDashArrival(out, scene, x, y, z);
  }

  prev.x = x;
  prev.z = z;
  prev.dashing = nowDashing;
}

/** Drop a cube's dash-track cache when it dies / leaves. */
export function clearDashTrailVfx(sessionId: string): void {
  dashTrackByCube.delete(sessionId);
}

// ---------------------------------------------------------------------------
// Task 10: Spell-cast proc buff floaters (Family 5)
//
// First-appearance detection: the manager tracks which cast-proc buffs were
// present on the previous frame per cube. On the frame a new one appears
// (present now but not before), spawn a colored "+proc" floater above the
// cube's head.
//
// Color map (matches the buff's thematic identity):
//   LiquidatorSpellbladeActive  → gold  (armed weapon)
//   RockAxeLifeRegen            → green (life regen)
//   MysticShieldBuff            → blue  (shield)
//   DismantleWeaponCharge       → orange (charge ready)
// ---------------------------------------------------------------------------

/** Buff id → floater color for cast-proc appearance. */
export const CAST_PROC_VFX: Readonly<Record<string, string>> = {
  LiquidatorSpellbladeActive: "#ffd700", // gold — blink-strike armed
  RockAxeLifeRegen:           "#4dff88", // green — life regen stack
  MysticShieldBuff:           "#7ecfff", // blue — shield applied
  DismantleWeaponCharge:      "#ffaa33", // orange — charge loaded
};

/** Per-cube: which cast-proc buff IDs were present on the previous frame. */
const lastCastProcBuffsByCube = new Map<string, Set<string>>();

/**
 * Spawn a cast-proc activation floater (colored short text) at (screenX, screenY).
 * Self-removes after the CSS animation (0.9s). Uses `fl-cast-proc-floater` class.
 */
export function spawnCastProcFloater(
  layer: HTMLElement,
  screenX: number,
  screenY: number,
  color: string,
): void {
  const el = document.createElement("div");
  el.className = "fl-cast-proc-floater";
  el.style.left = `${Math.round(screenX)}px`;
  el.style.top = `${Math.round(screenY)}px`;
  el.style.color = color;
  el.textContent = "✦";
  layer.appendChild(el);
  window.setTimeout(() => el.remove(), 950);
}

/**
 * Per-frame cast-proc proc detector. Call once per cube per frame.
 * Detects first appearance of any cast-proc buff (LiquidatorSpellbladeActive,
 * RockAxeLifeRegen, MysticShieldBuff, DismantleWeaponCharge) and spawns a
 * colored floater on the frame it arrives.
 *
 * @param cube      The cube schema (reads `cube.spellbook.buffs`).
 * @param sessionId Cube key (for the per-cube buff-set cache).
 * @param layer     The `#floaters-layer` DOM element.
 * @param screenX   Projected screen X of the unit's head.
 * @param screenY   Projected screen Y of the unit's head.
 */
export function tickCastProcVfx(
  cube: Cube,
  sessionId: string,
  layer: HTMLElement | null,
  screenX: number,
  screenY: number,
): void {
  const prev = lastCastProcBuffsByCube.get(sessionId) ?? new Set<string>();
  const current = new Set<string>();

  cube.spellbook?.buffs?.forEach((_inst, buffId) => {
    if (buffId in CAST_PROC_VFX) current.add(buffId);
  });

  // Spawn a floater for each newly-appeared cast-proc buff.
  if (layer) {
    current.forEach((buffId) => {
      if (!prev.has(buffId)) {
        const color = CAST_PROC_VFX[buffId];
        if (color) spawnCastProcFloater(layer, screenX, screenY - 20, color);
      }
    });
  }

  lastCastProcBuffsByCube.set(sessionId, current);
}

/** Drop a cube's cast-proc buff cache when it dies / leaves (manager calls on cull). */
export function clearCastProcVfx(sessionId: string): void {
  lastCastProcBuffsByCube.delete(sessionId);
}
